import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import _ from 'lodash';
import { ReplaySubject } from 'rxjs';
import { NavigationParams } from 'src/app/models/navigation/navigationParams.interface';
import { FiltersService } from '../filters/filters.service';
import { RoutesQueryParamsFactory } from './implementations/routeQueryParamsFactory';
import { IRoutesQueryParamsFactory } from './implementations/routeQueryParamsFactory.inteface';

class SupportedQueryParams {
  public integration = '';
  public violation = '';
  public resource = '';
  public policy = '';
  public platform = '';
  public agentStep = '';
  public issue = '';
  public finding = '';
  public findingThreadId = '';
  public stepThreadId = '';
  public preventionId = '';
  public inLineControl = '';
  public dataItem = '';
  public inline = '';

  // The q will be equal to the filters that used in the page that we enter.
  // The format of the q will be a bit different from what we are used to.
  // The format will be like -> q=name:value name:value name:value
  public q = '';
}
@Injectable({
  providedIn: 'root',
})
export class QueryParamsService {
  public toNavigate$ = new ReplaySubject<NavigationParams>(1);
  private readonly _routesQueryParamsFactory: IRoutesQueryParamsFactory;
  private _lastFilters: SupportedQueryParams = new SupportedQueryParams();
  private _lastRoute: string = this._router.url.split('?')[0];
  private _lastTab = '';

  constructor(
    private readonly _filtersService: FiltersService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute
  ) {
    this._routesQueryParamsFactory = new RoutesQueryParamsFactory(_router);

    this._activatedRoute.queryParams.subscribe((params: Params) => {
      if (Object.keys(params).length == 0) {
        this._filtersService.updateFiltersFromQuery('');
        this._lastFilters = new SupportedQueryParams();
        this.toNavigate$.next({});
      } else {
        const supportedQueryParams = this._getSupportedQueryParamsFromParams(params);
        const currentTab = params['tab'] ?? '';
        if (
          !_.isEqual(this._lastFilters, supportedQueryParams) ||
          this._router.url.split('?')[0] !== this._lastRoute ||
          this._lastTab.toLowerCase() !== currentTab.toLowerCase()
        ) {
          if (
            this._lastFilters.q !== supportedQueryParams.q ||
            this._lastTab.toLowerCase() !== currentTab.toLowerCase() ||
            this._lastRoute !== this._router.url.split('?')[0]
          ) {
            this._filtersService.updateFiltersFromQuery(supportedQueryParams.q, params['tab']);
          }
          this._triggerNavigationIfNeeded(supportedQueryParams);
          this._lastFilters = supportedQueryParams;
        }
      }
      this._lastTab = params['tab'] ?? '';
      this._lastRoute = this._router.url.split('?')[0];
    });
  }

  private _getNavigationParamsFromSupportedQueryParams(supportedQueryParams: SupportedQueryParams): NavigationParams {
    const navigationParams: { [key: string]: string } = {};

    Object.getOwnPropertyNames(supportedQueryParams).forEach((propertyName: string) => {
      // Exclude non-routing params from navigation triggering
      if (propertyName !== 'q' && propertyName !== 'inline') {
        const propertyValue = _.get(supportedQueryParams, propertyName);
        if (propertyValue != '') {
          navigationParams[propertyName] = propertyValue;
        }
      }
    });

    return navigationParams as NavigationParams;
  }

  /**
   * This method will try to parse the supported params.
   * If the params are valid then it will return them.
   * If they are not valid then it will clear the query params
   * @param rawParams The received raw params
   */
  private _getSupportedQueryParamsFromParams(rawParams: Params): SupportedQueryParams {
    const newParams: SupportedQueryParams = new SupportedQueryParams();

    Object.keys(rawParams).forEach((key: string) => {
      const value = rawParams[key];
      if (key && value && key in this._lastFilters) {
        const tempDict: { [key: string]: string } = {};
        tempDict[key] = value;
        Object.assign(newParams, tempDict);
      }
    });
    return newParams;
  }

  private _triggerNavigationIfNeeded(supportedQueryParams: SupportedQueryParams) {
    const navigationParamsFromQuery = this._getNavigationParamsFromSupportedQueryParams(supportedQueryParams);

    if (Object.keys(navigationParamsFromQuery).length > 0) {
      const verifiedNavigationParams: NavigationParams | undefined =
        this._routesQueryParamsFactory.create(navigationParamsFromQuery);

      if (verifiedNavigationParams) {
        this.toNavigate$.next(verifiedNavigationParams);
      } else {
        // Preserve existing query params (like stacked panel params s0, s1, etc.)
        // that may not be recognized by this service
        this._router.navigate([], {
          relativeTo: this._activatedRoute,
          queryParamsHandling: 'preserve',
        });
      }
    } else {
      this.toNavigate$.next({});
    }
  }
}
